import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchForm } from '@/store/slices/formSlice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ArrowLeft, Edit } from 'lucide-react';

const FormView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentForm, loading } = useSelector(state => state.forms);

  useEffect(() => {
    if (id) {
      dispatch(fetchForm(id));
    }
  }, [id, dispatch]);

  // Helper function to parse options safely
  const parseOptions = (options) => {
    if (Array.isArray(options)) return options;
    if (typeof options === 'string') {
      try {
        return JSON.parse(options);
      } catch (e) {
        console.error('Failed to parse options:', e);
        return [];
      }
    }
    return [];
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  if (!currentForm) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Form not found</h2>
          <Button onClick={() => navigate('/admin/forms')} className="mt-4">
            Back to Forms
          </Button>
        </div>
      </div>
    );
  }

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text',
      email: 'Email',
      number: 'Number',
      date: 'Date',
      textarea: 'Text Area',
      select: 'Dropdown Select',
      checkbox: 'Checkbox',
      file: 'File Upload',
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/forms')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">{currentForm.name}</h1>
        </div>
        <Button onClick={() => navigate(`/admin/forms/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Form
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Form Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="text-sm">{currentForm.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="text-sm">{currentForm.description || 'No description'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <p className="text-sm">
                {currentForm.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Verification</label>
              <p className="text-sm">
                {currentForm.requires_verification ? (
                  <Badge variant="secondary">Required</Badge>
                ) : (
                  <Badge variant="outline">Not Required</Badge>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Terms and Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: currentForm.terms_content }}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Form Fields ({currentForm.fields?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {currentForm.fields && currentForm.fields.length > 0 ? (
              currentForm.fields.map((field, index) => {
                // Parse options for each field
                const parsedOptions = parseOptions(field.options);
                
                return (
                  <div key={field.id || index} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">{field.label}</h3>
                      <div className="flex space-x-2">
                        <Badge variant="outline">{getFieldTypeLabel(field.type)}</Badge>
                        {field.required && <Badge variant="destructive">Required</Badge>}
                      </div>
                    </div>
                    
                    {field.description && (
                      <p className="text-sm text-muted-foreground">{field.description}</p>
                    )}
                    
                    {field.placeholder && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Placeholder: </span>
                        {field.placeholder}
                      </p>
                    )}
                    
                    {field.type === 'select' && parsedOptions.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Options:</p>
                        <div className="flex flex-wrap gap-1">
                          {parsedOptions.map((option, optIndex) => (
                            <Badge key={optIndex} variant="secondary">
                              {option.label} ({option.value})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No fields configured for this form.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormView;